import { useState, useEffect } from "react";
import { Mail, Send, Inbox, ChevronDown, ChevronUp, ExternalLink, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface Email {
  id: string;
  subject: string | null;
  from_address: string;
  to_addresses: unknown; // JSON from database
  body_text: string | null;
  body_html: string | null;
  status: string | null;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
  is_read: boolean | null;
  attachments: unknown; // JSON from database
}

interface CustomerEmailHistoryProps {
  customerEmail: string;
  customerName: string;
  onComposeEmail: () => void;
  onViewEmail?: (email: Email) => void;
}

export const CustomerEmailHistory = ({ 
  customerEmail, 
  customerName,
  onComposeEmail,
  onViewEmail
}: CustomerEmailHistoryProps) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  useEffect(() => {
    if (customerEmail) {
      loadEmailHistory();
    } else {
      setEmails([]);
      setLoading(false);
    }
  }, [customerEmail]);

  const loadEmailHistory = async () => {
    if (!customerEmail) return;
    
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get emails where customer email is in to_addresses OR from_address
      const { data, error } = await supabase
        .from('emails')
        .select('*')
        .eq('user_id', user.id)
        .or(`from_address.ilike.%${customerEmail}%,to_addresses.cs.["${customerEmail}"]`)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading emails:', error);
        return;
      }

      // Filter emails that actually match the customer email
      const filteredEmails = (data || []).filter(email => {
        const fromMatch = email.from_address?.toLowerCase().includes(customerEmail.toLowerCase());
        const toAddresses = Array.isArray(email.to_addresses) ? email.to_addresses : [];
        const toMatch = toAddresses.some((addr: unknown) => 
          typeof addr === 'string' && addr.toLowerCase().includes(customerEmail.toLowerCase())
        );
        return fromMatch || toMatch;
      });

      setEmails(filteredEmails as Email[]);
    } catch (error) {
      console.error('Error loading email history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmailDirection = (email: Email): 'sent' | 'received' => {
    return email.status === 'sent' ? 'sent' : 'received';
  };

  const formatEmailDate = (email: Email) => {
    const dateStr = email.sent_at || email.received_at || email.created_at;
    if (!dateStr) return '';
    
    try {
      const date = new Date(dateStr);
      return format(date, "dd.MM.yyyy 'um' HH:mm", { locale: de });
    } catch {
      return '';
    }
  };

  const getPreviewText = (email: Email) => {
    const text = email.body_text || '';
    if (text.length > 100) {
      return text.substring(0, 100) + '...';
    }
    return text;
  };

  if (!customerEmail) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        Keine E-Mail-Adresse hinterlegt
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Compose Button */}
      <Button 
        variant="outline" 
        size="sm" 
        onClick={onComposeEmail}
        className="w-full gap-2"
      >
        <Mail className="h-4 w-4" />
        E-Mail an {customerName.split(' ')[0]} schreiben
      </Button>

      {/* Email History Collapsible */}
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <Button 
            variant="ghost" 
            className="w-full justify-between p-2 h-auto hover:bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">E-Mail-Verlauf</span>
              <Badge variant="outline" className="text-xs">
                {loading ? '...' : emails.length}
              </Badge>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="mt-2">
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              Lade E-Mails...
            </div>
          ) : emails.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">
              Noch keine E-Mails mit diesem Kunden
            </div>
          ) : (
            <ScrollArea className="h-[300px]">
              <div className="space-y-2 pr-2">
                {emails.map((email) => {
                  const direction = getEmailDirection(email);
                  const isEmailExpanded = expandedEmailId === email.id;
                  
                  return (
                    <div 
                      key={email.id}
                      className={cn(
                        "border rounded-lg p-3 transition-colors cursor-pointer hover:bg-muted/50",
                        !email.is_read && direction === 'received' && "bg-primary/5 border-primary/20"
                      )}
                      onClick={() => setExpandedEmailId(isEmailExpanded ? null : email.id)}
                    >
                      <div className="flex items-start gap-2">
                        <div className={cn(
                          "mt-0.5 p-1 rounded",
                          direction === 'sent' ? "bg-blue-100 text-blue-600" : "bg-green-100 text-green-600"
                        )}>
                          {direction === 'sent' ? (
                            <Send className="h-3 w-3" />
                          ) : (
                            <Inbox className="h-3 w-3" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-muted-foreground">
                              {direction === 'sent' ? 'Gesendet' : 'Empfangen'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatEmailDate(email)}
                            </span>
                            {email.attachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
                              <Paperclip className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                          
                          <p className="font-medium text-sm truncate">
                            {email.subject || '(Kein Betreff)'}
                          </p>
                          
                          {!isEmailExpanded && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                              {getPreviewText(email)}
                            </p>
                          )}
                          
                          {isEmailExpanded && (
                            <div className="mt-3 pt-3 border-t">
                              <div 
                                className="text-sm prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ 
                                  __html: email.body_html || email.body_text?.replace(/\n/g, '<br>') || '' 
                                }}
                              />
                              
                              {onViewEmail && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-2 p-0 h-auto text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onViewEmail(email);
                                  }}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Im E-Mail-Client öffnen
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
